import React, { useMemo } from 'react';
import {
    User,
    UsersThree,
    MapPin,
    Package,
    CaretRight,
    Graph,
    Icon,
    ArrowRight
} from '@phosphor-icons/react';
import { useIdea } from '../../context/IdeaContext';
import { Dynamic, DynamicType } from '../../types';

const DYNAMIC_ICONS: Record<DynamicType, Icon> = {
    character: User,
    relationship: UsersThree,
    location: MapPin,
    prop: Package
};

// Premiere Pro inspired muted colors
const DYNAMIC_STYLES: Record<DynamicType, { text: string; bg: string; border: string; hover: string }> = {
    character: {
        text: 'text-entity-soft',
        bg: 'bg-entity/10',
        border: 'border-entity/25',
        hover: 'hover:bg-entity/15'
    },
    relationship: {
        text: 'text-relationship-soft',
        bg: 'bg-relationship/10',
        border: 'border-relationship/25',
        hover: 'hover:bg-relationship/15'
    },
    location: {
        text: 'text-place-soft',
        bg: 'bg-place/10',
        border: 'border-place/25',
        hover: 'hover:bg-place/15'
    },
    prop: {
        text: 'text-thing-soft',
        bg: 'bg-thing/10',
        border: 'border-thing/25',
        hover: 'hover:bg-thing/15'
    }
};

export const DynamicsPanel: React.FC = () => {
    const { dynamics, workspaceState, selectDynamic } = useIdea();

    // Group dynamics by type
    const groupedDynamics = useMemo(() => {
        const groups: Record<DynamicType, Dynamic[]> = {
            character: [],
            relationship: [],
            location: [],
            prop: []
        };
        dynamics.forEach(d => {
            groups[d.type].push(d);
        });
        return groups;
    }, [dynamics]);

    // Check if we have any dynamics
    const hasDynamics = dynamics.length > 0;

    // Premiere Pro style panel
    return (
        <div className="flex-1 flex flex-col p-2 space-y-3">
            {/* Empty State - Premiere style */}
            {!hasDynamics && (
                <div className="p-4 rounded border border-iron/40 bg-onyx text-center">
                    <Graph className="w-6 h-6 text-iron mx-auto mb-2" />
                    <p className="text-[10px] text-gray-500 leading-relaxed">
                        Connect cards to entities to visualize story dynamics here.
                    </p>
                </div>
            )}

            {hasDynamics && (
                <>
                    {/* Characters */}
                    <DynamicGroup
                        type="character"
                        label="Characters"
                        dynamics={groupedDynamics.character}
                        selectedKey={workspaceState.selectedDynamic?.key}
                        onSelect={selectDynamic}
                    />

                    {/* Relationships */}
                    <DynamicGroup
                        type="relationship"
                        label="Relationships"
                        dynamics={groupedDynamics.relationship}
                        selectedKey={workspaceState.selectedDynamic?.key}
                        onSelect={selectDynamic}
                    />

                    {/* Locations */}
                    <DynamicGroup
                        type="location"
                        label="Locations"
                        dynamics={groupedDynamics.location}
                        selectedKey={workspaceState.selectedDynamic?.key}
                        onSelect={selectDynamic}
                    />

                    {/* Props */}
                    <DynamicGroup
                        type="prop"
                        label="Props"
                        dynamics={groupedDynamics.prop}
                        selectedKey={workspaceState.selectedDynamic?.key}
                        onSelect={selectDynamic}
                    />
                </>
            )}
        </div>
    );
};

interface DynamicGroupProps {
    type: DynamicType;
    label: string;
    dynamics: Dynamic[];
    selectedKey?: string;
    onSelect: (dynamic: Dynamic | null) => void;
}

// Premiere Pro style group
const DynamicGroup: React.FC<DynamicGroupProps> = ({
    type,
    label,
    dynamics,
    selectedKey,
    onSelect
}) => {
    if (dynamics.length === 0) return null;

    const Icon = DYNAMIC_ICONS[type];
    const styles = DYNAMIC_STYLES[type];

    return (
        <div className="space-y-1.5">
            <div className="flex items-center gap-2 px-1">
                <Icon className={`w-3 h-3 ${styles.text} opacity-60`} weight="fill" />
                <span className="text-[9px] font-medium text-gray-500 uppercase tracking-wider">
                    {label}
                </span>
                <div className="h-px flex-1 bg-iron/30" />
            </div>

            <div className="space-y-1">
                {dynamics.map(dynamic => (
                    <DynamicItem
                        key={dynamic.key}
                        dynamic={dynamic}
                        isSelected={selectedKey === dynamic.key}
                        onSelect={() => onSelect(dynamic)}
                    />
                ))}
            </div>
        </div>
    );
};

interface DynamicItemProps {
    dynamic: Dynamic;
    isSelected: boolean;
    onSelect: () => void;
}

// Premiere Pro style item
const DynamicItem: React.FC<DynamicItemProps> = ({
    dynamic,
    isSelected,
    onSelect
}) => {
    const Icon = DYNAMIC_ICONS[dynamic.type];
    const styles = DYNAMIC_STYLES[dynamic.type];

    return (
        <button
            onClick={onSelect}
            className={`
                w-full group relative flex items-center gap-2.5 px-2.5 py-2 rounded
                border transition-all text-left
                ${isSelected
                    ? `${styles.bg} ${styles.border}`
                    : 'bg-onyx border-iron/40 hover:border-iron hover:bg-zinc-900'
                }
            `}
        >
            {/* Accent line on left when selected */}
            {isSelected && (
                <div className={`absolute left-0 top-1/4 bottom-1/4 w-0.5 rounded-r ${styles.bg.replace('/10', '')}`} />
            )}

            <div className={`
                w-6 h-6 rounded flex items-center justify-center flex-shrink-0 transition-colors
                ${isSelected ? styles.bg : 'bg-graphite'}
            `}>
                <Icon
                    className={`w-3.5 h-3.5 ${isSelected ? styles.text : 'text-gray-500 group-hover:text-gray-400'}`}
                    weight={isSelected ? 'fill' : 'duotone'}
                />
            </div>

            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                    <span className={`text-[11px] font-medium truncate ${isSelected ? 'text-neutral-200' : 'text-gray-400 group-hover:text-neutral-200'}`}>
                        {dynamic.label}
                    </span>
                    {isSelected && <ArrowRight className={`w-3 h-3 ${styles.text}`} />}
                </div>

                <span className={`text-[9px] ${isSelected ? 'text-gray-400' : 'text-gray-500'}`}>
                    {dynamic.instanceCount} card{dynamic.instanceCount !== 1 ? 's' : ''}
                </span>
            </div>
        </button>
    );
};

